import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Map,
  Database,
  Bell,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  Shield,
  Satellite,
  Activity,
  History,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// ----------------------------------------------------------
// Nav item definition
// ----------------------------------------------------------

interface NavItem {
  label: string
  path: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  badge?: string
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Mappa Rischio', path: '/risk', icon: Map },
  { label: 'Dati & Sensori', path: '/data', icon: Database },
  { label: 'Allerte', path: '/alerts', icon: Bell, badge: '3' },
  { label: 'Modelli ML', path: '/ml', icon: BrainCircuit },
  { label: 'Retrocast', path: '/retrocast', icon: History },
]

// ----------------------------------------------------------
// Props
// ----------------------------------------------------------

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

// ----------------------------------------------------------
// Component
// ----------------------------------------------------------

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        'relative flex flex-col h-screen z-30 flex-shrink-0',
        'bg-gradient-to-b from-slate-900 via-[#0d1526] to-terra-dark',
        'border-r border-terra-blue/10'
      )}
    >
      {/* Subtle gradient accent at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-terra-blue/40 to-transparent" />

      {/* ---- Logo ---- */}
      <div className="flex items-center h-16 px-4 border-b border-slate-800/60 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {/* Icon mark */}
          <div className="relative flex-shrink-0">
            <motion.div
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.2 }}
              className="w-8 h-8 rounded-lg bg-gradient-to-br from-terra-blue/25 to-terra-purple/15 border border-terra-blue/40 flex items-center justify-center"
              style={{ boxShadow: '0 0 12px rgba(14,165,233,0.15)' }}
            >
              <Shield size={16} className="text-terra-blue" />
            </motion.div>
            {/* Live pulse */}
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-terra-green border-2 border-slate-900">
              <span className="absolute inset-0 rounded-full bg-terra-green animate-ping-slow opacity-50" />
            </span>
          </div>

          <AnimatePresence>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden whitespace-nowrap"
              >
                <span className="text-base font-bold tracking-wider gradient-text-terra">
                  TERRA
                </span>
                <span className="text-terra-blue font-bold">.</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ---- System status ---- */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="px-4 py-2 border-b border-slate-800/40 flex-shrink-0"
          >
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Satellite size={11} className="text-terra-green flex-shrink-0" />
              <span className="truncate">Sentinel-1C</span>
              <Activity size={11} className="text-terra-green ml-auto flex-shrink-0" />
              <span className="text-terra-green font-medium">LIVE</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Navigation ---- */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
          const Icon = item.icon

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                'nav-item relative group',
                isActive && 'active'
              )}
              title={collapsed ? item.label : undefined}
            >
              {/* Active indicator bar */}
              {isActive && (
                <motion.div
                  layoutId="activeBar"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r"
                  style={{
                    background: 'linear-gradient(180deg, #0ea5e9, #8b5cf6)',
                    boxShadow: '0 0 8px rgba(14,165,233,0.4)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}

              <Icon
                size={18}
                className={cn(
                  'flex-shrink-0 transition-all duration-200',
                  isActive ? 'text-terra-blue drop-shadow-[0_0_6px_rgba(14,165,233,0.5)]' : 'text-slate-500 group-hover:text-slate-300'
                )}
              />

              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden whitespace-nowrap flex-1 text-sm"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>

              {/* Badge */}
              {item.badge && !collapsed && (
                <span className="flex-shrink-0 text-xs font-bold bg-terra-red text-white rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ boxShadow: '0 0 8px rgba(239,68,68,0.5)' }}
                >
                  {item.badge}
                </span>
              )}
              {item.badge && collapsed && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-terra-red"
                  style={{ boxShadow: '0 0 6px rgba(239,68,68,0.5)' }}
                />
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* ---- Footer ---- */}
      <div className="px-2 pb-4 flex-shrink-0 border-t border-slate-800/60 pt-4">
        {/* Version */}
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="px-3 py-2 mb-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600 font-mono">v0.1.0-poc</span>
                <span className="text-xs bg-terra-purple/20 text-terra-purple border border-terra-purple/30 rounded px-1.5 py-0.5">
                  POC
                </span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5 truncate">
                WhereTech S.r.l.
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className={cn(
            'w-full flex items-center rounded-lg px-3 py-2 text-slate-500',
            'hover:text-slate-300 hover:bg-slate-800/60 transition-all duration-200',
            collapsed ? 'justify-center' : 'justify-between'
          )}
          title={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
        >
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.span
                key="label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-xs"
              >
                Comprimi
              </motion.span>
            )}
          </AnimatePresence>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </motion.aside>
  )
}
